/* tslint:disable */
/* eslint-disable */
import { Injectable } from '@angular/core';
import { HttpClient, HttpResponse } from '@angular/common/http';
import { BaseService } from '../base-service';
import { ApiConfiguration } from '../api-configuration';
import { StrictHttpResponse } from '../strict-http-response';
import { RequestBuilder } from '../request-builder';
import { Observable } from 'rxjs';
import { map, filter } from 'rxjs/operators';

import { CreateProjectFolderDto } from '../models/create-project-folder-dto';
import { ProjectFolder } from '../models/project-folder';
import { ProjectFolderPartial } from '../models/project-folder-partial';
import { ProjectFolderWithRelations } from '../models/project-folder-with-relations';

@Injectable({
  providedIn: 'root',
})
export class ProjectFolderControllerService extends BaseService {
  constructor(
    config: ApiConfiguration,
    http: HttpClient
  ) {
    super(config, http);
  }

  /**
   * Path part for operation projectFolderControllerGetRootFolder
   */
  static readonly ProjectFolderControllerGetRootFolderPath = '/project-folders/get-root-folder';

  /**
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `getRootFolder()` instead.
   *
   * This method doesn't expect any request body.
   */
  getRootFolder$Response(params?: {
    filter?: any;
  }): Observable<StrictHttpResponse<ProjectFolderWithRelations>> {

    const rb = new RequestBuilder(this.rootUrl, ProjectFolderControllerService.ProjectFolderControllerGetRootFolderPath, 'get');
    if (params) {
      rb.query('filter', params.filter, {});
    }

    return this.http.request(rb.build({
      responseType: 'json',
      accept: 'application/json'
    })).pipe(
      filter((r: any) => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return r as StrictHttpResponse<ProjectFolderWithRelations>;
      })
    );
  }

  /**
   * This method provides access to only to the response body.
   * To access the full response (for headers, for example), `getRootFolder$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  getRootFolder(params?: {
    filter?: any;
  }): Observable<ProjectFolderWithRelations> {

    return this.getRootFolder$Response(params).pipe(
      map((r: StrictHttpResponse<ProjectFolderWithRelations>) => r.body as ProjectFolderWithRelations)
    );
  }

  /**
   * Path part for operation projectFolderControllerFindById
   */
  static readonly ProjectFolderControllerFindByIdPath = '/project-folders/{id}';

  /**
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `findById()` instead.
   *
   * This method doesn't expect any request body.
   */
  findById$Response(params: {
    id: string;
    filter?: any;
  }): Observable<StrictHttpResponse<ProjectFolder>> {

    const rb = new RequestBuilder(this.rootUrl, ProjectFolderControllerService.ProjectFolderControllerFindByIdPath, 'get');
    if (params) {
      rb.path('id', params.id, {});
      rb.query('filter', params.filter, {});
    }

    return this.http.request(rb.build({
      responseType: 'json',
      accept: 'application/json'
    })).pipe(
      filter((r: any) => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return r as StrictHttpResponse<ProjectFolder>;
      })
    );
  }

  /**
   * This method provides access to only to the response body.
   * To access the full response (for headers, for example), `findById$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  findById(params: {
    id: string;
    filter?: any;
  }): Observable<ProjectFolder> {

    return this.findById$Response(params).pipe(
      map((r: StrictHttpResponse<ProjectFolder>) => r.body as ProjectFolder)
    );
  }

  /**
   * Path part for operation projectFolderControllerDeleteById
   */
  static readonly ProjectFolderControllerDeleteByIdPath = '/project-folders/{id}';

  /**
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `deleteById()` instead.
   *
   * This method doesn't expect any request body.
   */
  deleteById$Response(params: {
    id: string;
  }): Observable<StrictHttpResponse<void>> {

    const rb = new RequestBuilder(this.rootUrl, ProjectFolderControllerService.ProjectFolderControllerDeleteByIdPath, 'delete');
    if (params) {
      rb.path('id', params.id, {});
    }

    return this.http.request(rb.build({
      responseType: 'text',
      accept: '*/*'
    })).pipe(
      filter((r: any) => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return (r as HttpResponse<any>).clone({ body: undefined }) as StrictHttpResponse<void>;
      })
    );
  }

  /**
   * This method provides access to only to the response body.
   * To access the full response (for headers, for example), `deleteById$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  deleteById(params: {
    id: string;
  }): Observable<void> {

    return this.deleteById$Response(params).pipe(
      map((r: StrictHttpResponse<void>) => r.body as void)
    );
  }

  /**
   * Path part for operation projectFolderControllerUpdateById
   */
  static readonly ProjectFolderControllerUpdateByIdPath = '/project-folders/{id}';

  /**
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `updateById()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  updateById$Response(params: {
    id: string;
    body?: ProjectFolderPartial
  }): Observable<StrictHttpResponse<any>> {

    const rb = new RequestBuilder(this.rootUrl, ProjectFolderControllerService.ProjectFolderControllerUpdateByIdPath, 'patch');
    if (params) {
      rb.path('id', params.id, {});
      rb.body(params.body, 'application/json');
    }

    return this.http.request(rb.build({
      responseType: 'json',
      accept: 'application/json'
    })).pipe(
      filter((r: any) => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return r as StrictHttpResponse<any>;
      })
    );
  }

  /**
   * This method provides access to only to the response body.
   * To access the full response (for headers, for example), `updateById$Response()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  updateById(params: {
    id: string;
    body?: ProjectFolderPartial
  }): Observable<any> {

    return this.updateById$Response(params).pipe(
      map((r: StrictHttpResponse<any>) => r.body as any)
    );
  }

  /**
   * Path part for operation projectFolderControllerCreate
   */
  static readonly ProjectFolderControllerCreatePath = '/project-folders';

  /**
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `create()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  create$Response(params?: {
    body?: CreateProjectFolderDto
  }): Observable<StrictHttpResponse<ProjectFolder>> {

    const rb = new RequestBuilder(this.rootUrl, ProjectFolderControllerService.ProjectFolderControllerCreatePath, 'post');
    if (params) {
      rb.body(params.body, 'application/json');
    }

    return this.http.request(rb.build({
      responseType: 'json',
      accept: 'application/json'
    })).pipe(
      filter((r: any) => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return r as StrictHttpResponse<ProjectFolder>;
      })
    );
  }

  /**
   * This method provides access to only to the response body.
   * To access the full response (for headers, for example), `create$Response()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  create(params?: {
    body?: CreateProjectFolderDto
  }): Observable<ProjectFolder> {

    return this.create$Response(params).pipe(
      map((r: StrictHttpResponse<ProjectFolder>) => r.body as ProjectFolder)
    );
  }

}
